import { useEffect, useRef } from "react";

// 휴대폰 뒤로가기(또는 브라우저 뒤로가기)로 사진 크게 보기 같은 겹친 화면을 닫게 하는 장치.
// 열 때 기록을 하나 쌓고, 뒤로가기가 눌리면 닫는다. X 로 닫으면 쌓은 기록을 history.back() 으로 되돌린다.
//
// history.back() 은 바로 실행되지 않고 나중에 popstate 로 도착한다. 그 사이에 기록을 새로 쌓으면
// 브라우저마다 결과가 다르다 (Chrome 은 늦은 back 이 새 기록을 지우고, jsdom 은 back 자체가 취소된다).
// 그래서 우리가 부른 back 이 아직 도착하지 않았으면 새 기록을 바로 쌓지 않고, 도착한 뒤에 쌓는다.
let pendingBacks = 0;
let onBackPressed: (() => void) | null = null;
let pushWhenSettled = false;
let listenerInstalled = false;

const STATE_KEY = "vehiclePhotoZoom";

function pushEntry() {
  window.history.pushState({ [STATE_KEY]: true }, "");
}

function installListener() {
  if (listenerInstalled) return;
  listenerInstalled = true;
  window.addEventListener("popstate", () => {
    if (pendingBacks > 0) {
      // 우리가 부른 back 이 도착했다. 사용자가 누른 뒤로가기가 아니다.
      pendingBacks -= 1;
      if (pendingBacks === 0 && pushWhenSettled && onBackPressed) {
        pushWhenSettled = false;
        pushEntry();
      }
      return;
    }
    onBackPressed?.();
  });
}

export function useBackButtonToClose(open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    installListener();
    if (pendingBacks > 0) {
      pushWhenSettled = true;
    } else {
      pushEntry();
    }

    let closedByBack = false;
    const handler = () => {
      closedByBack = true;
      closeRef.current();
    };
    onBackPressed = handler;

    return () => {
      if (onBackPressed === handler) onBackPressed = null;
      if (closedByBack) return;
      if (pushWhenSettled) {
        // 아직 기록을 쌓기 전에 닫혔다. 되돌릴 것이 없다.
        pushWhenSettled = false;
        return;
      }
      if (window.history.state?.[STATE_KEY]) {
        pendingBacks += 1;
        window.history.back();
      }
    };
  }, [open]);
}

/** 테스트 전용. 모듈 상태를 처음으로 돌린다. */
export function resetBackButtonStateForTest() {
  pendingBacks = 0;
  onBackPressed = null;
  pushWhenSettled = false;
}
